import MidiNumbers from "components/piano/MidiNumbers";

export interface MajorScale {
  [key: string]: number[];
}

export interface Chord {
  root: string;
  notes: number[];
}

export const generateMajorScales = (): MajorScale => {
  // MIDI number for C4 (middle C)
  const C4 = 60;
  // Pattern for a major scale: W-W-H-W-W-W-H (W = whole step, H = half step)
  const scalePattern = [2, 2, 1, 2, 2, 2, 1];

  // List of all keys names, assuming we start from C and use sharps
  const keys = [
    "C",
    "C#",
    "D",
    "D#",
    "E",
    "F",
    "F#",
    "G",
    "G#",
    "A",
    "A#",
    "B",
  ];

  const scales: MajorScale = {};

  keys.forEach((key, index) => {
    let noteMidiNumber = C4 + index; // Starting MIDI number for the key
    let scaleNotes = [noteMidiNumber]; // First note of the scale

    scalePattern.forEach((step) => {
      noteMidiNumber += step;
      scaleNotes.push(noteMidiNumber);
    });

    scales[key] = scaleNotes;
  });

  return scales;
};

// Generates the major scale for a given MIDI number root
export const generateMajorScale = (keyRoot: number): number[] => {
  // Pattern for a major scale: W-W-H-W-W-W-H (W = whole step, H = half step)
  const scalePattern = [2, 2, 1, 2, 2, 2, 1];
  let noteMidiNumber = keyRoot; // Starting MIDI number for the key
  let scaleNotes = [noteMidiNumber]; // First note of the scale

  scalePattern.forEach((step) => {
    noteMidiNumber += step;
    scaleNotes.push(noteMidiNumber);
  });

  return scaleNotes;
};

export const chordIntervals: { [key: string]: number[] } = {
  major: [0, 4, 7],
  minor: [0, 3, 7],
  augmented: [0, 4, 8],
  diminished: [0, 3, 6],

  dominant_seventh: [0, 4, 7, 10],
  major_seventh: [0, 4, 7, 11],
  minor_major_seventh: [0, 3, 7, 11],
  minor_seventh: [0, 3, 7, 10],
  augmented_major_seventh: [0, 4, 8, 11],
  augmented_seventh: [0, 4, 8, 10],
  half_diminished_seventh: [0, 3, 6, 10],
  diminished_seventh: [0, 3, 6, 9],
  dominant_seventh_flat_five: [0, 4, 6, 10],

  ninth: [0, 4, 7, 10, 14],
  major_ninth: [0, 4, 7, 11, 14],
  dominant_ninth: [0, 4, 7, 10, 14],
  dominant_minor_ninth: [0, 4, 7, 10, 13],
  minor_major_ninth: [0, 3, 7, 11, 14],
  minor_ninth: [0, 3, 7, 10, 14],
  augmented_major_ninth: [0, 4, 8, 11, 14],
  augmented_dominant_ninth: [0, 4, 8, 10, 14],
  half_diminished_ninth: [0, 3, 6, 10, 14],
  half_diminished_minor_ninth: [0, 3, 6, 10, 13],
  diminished_ninth: [0, 3, 6, 9, 14],
  diminished_minor_ninth: [0, 3, 6, 9, 13],

  eleventh: [0, 4, 7, 10, 14, 17],
  major_eleventh: [0, 4, 7, 11, 14, 17],
  minor_major_eleventh: [0, 3, 7, 11, 14, 17],
  minor_eleventh: [0, 3, 7, 10, 14, 17],
  augmented_major_eleventh: [0, 4, 8, 11, 14, 17],
  augmented_eleventh: [0, 4, 8, 10, 14, 17],
  half_diminished_eleventh: [0, 3, 6, 10, 14, 17],
  diminished_eleventh: [0, 3, 6, 9, 14, 17],

  thirteenth: [0, 4, 7, 10, 14, 17, 21],
  major_thirteenth: [0, 4, 7, 11, 14, 17, 21],
  minor_major_thirteenth: [0, 3, 7, 11, 14, 17, 21],
  minor_thirteenth: [0, 3, 7, 10, 14, 17, 21],
  augmented_major_thirteenth: [0, 4, 8, 11, 14, 17, 21],
  augmented_thirteenth: [0, 4, 8, 10, 14, 17, 21],
  half_diminished_thirteenth: [0, 3, 6, 10, 14, 17, 21],
};

export const generateChord = (root: number, chordType: string): Chord => {
  const intervals = chordIntervals[chordType];
  const notes = intervals.map((interval) => root + interval);
  return {
    root: MidiNumbers.getNoteName(root),
    notes: notes,
  };
};

export const getIntervalNote = (root: number, interval: number): number => {
  return root + interval;
};

export const identifyInterval = (note1: number, note2: number): string => {
  const interval = Math.abs(note2 - note1);
  const intervals: { [key: number]: string } = {
    0: "Unison",
    1: "Minor Second",
    2: "Major Second",
    3: "Minor Third",
    4: "Major Third",
    5: "Perfect Fourth",
    6: "Tritone",
    7: "Perfect Fifth",
    8: "Minor Sixth",
    9: "Major Sixth",
    10: "Minor Seventh",
    11: "Major Seventh",
    12: "Octave",
    13: "Minor Ninth",
    14: "Major Ninth",
    15: "Minor Tenth",
    16: "Major Tenth",
    17: "Perfect Eleventh",
    18: "Augmented Eleventh",
    20: "Minor Thirteenth",
    21: "Major Thirteenth",
  };
  return intervals[interval] || `Interval of ${interval} semitones`;
};
